/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  DeviceConfigurationService,
  type TServicesParams
} from "@tivocorp/data-domain-lib";

import { Log } from "../logging/Log";
import { BrandingUiInfoKeys } from "../entities/BrandingUiInfoKeys";

interface BrandingData {
  [key: string]: unknown;
  appImages?: Array<{ imageIcon: string; imageType: string; imageUrl: string }>;
}

export class Branding {
  private static readonly TAG = "Branding";

  private static brandingMap: BrandingData = {};

  static readonly setFallbackBrandings = async (
    locale: string,
    msoName?: string
  ): Promise<void> => {
    const localeArr = locale.split("_");
    const language = localeArr[0].toLowerCase();
    const region = localeArr[1].toLowerCase();
    const msoNameLowerCase =
      msoName !== null && msoName !== undefined
        ? msoName.toLowerCase()
        : undefined;
    let languageFile: BrandingData = {};
    let regionLanguageFile: BrandingData = {};
    let msoLanguageFile: BrandingData = {};

    // Load default branding file (en)
    try {
      Branding.brandingMap = await import(
        `../assets/brandings/en_any_any_unmanaged_smarttv.json`
      );
    } catch (err: unknown) {
      const error = err as Error;
      Log.e(Branding.TAG, error.message);
    }

    // Load language-specific file
    try {
      languageFile = await import(
        `../assets/brandings/${language}_any_any_unmanaged_smarttv.json`
      );
    } catch (err: unknown) {
      const error = err as Error;
      Log.e(Branding.TAG, error.message);
    }

    // Load region + language file
    try {
      regionLanguageFile = await import(
        `../assets/brandings/${language}_${region}_any_unmanaged_smarttv.json`
      );
    } catch (err: unknown) {
      const error = err as Error;
      Log.e(Branding.TAG, error.message);
    }

    // Load mso + language file
    if (msoNameLowerCase !== undefined) {
      try {
        msoLanguageFile = await import(
          `../assets/brandings/${language}_${region}_${msoNameLowerCase}_unmanaged_smarttv.json`
        );
      } catch (err: unknown) {
        const error = err as Error;
        Log.e(Branding.TAG, error.message);
      }
    }

    // Cascading merge: default < language < region < mso
    Branding.brandingMap = Branding.mergeObjects(
      Branding.brandingMap,
      languageFile
    );
    Branding.brandingMap = Branding.mergeObjects(
      Branding.brandingMap,
      regionLanguageFile
    );
    Branding.brandingMap = Branding.mergeObjects(
      Branding.brandingMap,
      msoLanguageFile
    );

    Branding.applyStringOverrides();
  };

  /**
   * Once `brandingMap` is built, walks its keys and replaces any value that
   * has a live string-override (fetched via
   * `DeviceConfigurationService.fetchMsoDefaultStringOverrides` /
   * `fetchStringOverrides`) — same live-over-fallback precedence as
   * `Locale.getString` — baking the override into the map once instead of
   * re-checking it on every `getBrandingUiInfoForKey` call.
   */
  private static readonly applyStringOverrides = (): void => {
    try {
      const service: DeviceConfigurationService =
        new DeviceConfigurationService({} as TServicesParams);
      Object.keys(Branding.brandingMap).forEach((key) => {
        const overrideValue: string | undefined =
          service.getStringOverrideForKey(key);
        if (
          overrideValue !== undefined &&
          overrideValue !== null &&
          overrideValue !== ""
        ) {
          Branding.brandingMap[key] = overrideValue;
        }
      });
    } catch (err: unknown) {
      const error = err as Error;
      Log.e(Branding.TAG, error.message);
    }
  };

  static readonly getFallbackBrandingUiInfoForKey = (key: string): unknown => {
    return Branding.brandingMap[key];
  };

  /**
   * Reads a branding UI value for `key`, preferring the live MSO branding
   * bundle (fetched post-signin via `MsoBrandingUiService.fetchBrandingUiBundle`)
   * and falling back to the locally bundled fallback JSON files — which
   * `applyStringOverrides` has already updated with any live string
   * overrides — when the live value is unavailable.
   */
  static readonly getBrandingUiInfoForKey = (key: string): unknown => {
    try {
      const service: DeviceConfigurationService =
        new DeviceConfigurationService({} as TServicesParams);
      const brandingValue: unknown = service.getBrandingUiInfoForKey(key);
      if (
        brandingValue !== undefined &&
        brandingValue !== null &&
        brandingValue !== ""
      ) {
        return brandingValue;
      }
    } catch (err: unknown) {
      const error = err as Error;
      Log.e(Branding.TAG, error.message);
    }

    return Branding.getFallbackBrandingUiInfoForKey(key);
  };

  /**
   * Replaces the `{{phoneNumber}}`, `{{customerSupportName}}`, and
   * `{{urlCustomerSupport}}` placeholders in `text` with the branded
   * customer-support details. A placeholder that isn't present in `text` is
   * left untouched.
   */
  static readonly applyCustomerSupportPlaceholders = (text: string): string => {
    return text
      .replace(
        "{{phoneNumber}}",
        Branding.getFallbackBrandingUiInfoForKey(
          BrandingUiInfoKeys.PHONE_CUSTOMER_SUPPORT_NUMBER
        ) as string
      )
      .replace(
        "{{customerSupportName}}",
        Branding.getFallbackBrandingUiInfoForKey(
          BrandingUiInfoKeys.CUSTOMER_SUPPORT_NAME
        ) as string
      )
      .replace(
        "{{urlCustomerSupport}}",
        Branding.getFallbackBrandingUiInfoForKey(
          BrandingUiInfoKeys.URL_CUSTOMER_SUPPORT
        ) as string
      );
  };

  private static mergeArrays(
    targetValue: unknown,
    sourceValue: unknown[]
  ): unknown[] {
    if (!Array.isArray(targetValue)) {
      return sourceValue;
    }
    const merged = [...targetValue, ...sourceValue];
    const seen = new Set<string>();
    const deduped: unknown[] = [];
    for (const item of merged) {
      const serialized = JSON.stringify(item);
      if (!seen.has(serialized)) {
        seen.add(serialized);
        deduped.push(item);
      }
    }
    return deduped;
  }

  private static mergeValue(
    targetValue: unknown,
    sourceValue: unknown
  ): unknown {
    if (Array.isArray(sourceValue)) {
      return Branding.mergeArrays(targetValue, sourceValue);
    }
    if (
      typeof sourceValue === "object" &&
      typeof targetValue === "object" &&
      targetValue !== null &&
      !Array.isArray(targetValue)
    ) {
      return Branding.mergeObjects(
        targetValue as BrandingData,
        sourceValue as BrandingData
      );
    }
    return sourceValue;
  }

  private static mergeObjects(
    target: BrandingData,
    source: BrandingData
  ): BrandingData {
    const result: BrandingData = { ...target };

    Object.keys(source).forEach((key) => {
      const sourceValue = source[key];
      if (sourceValue !== null && sourceValue !== undefined) {
        result[key] = Branding.mergeValue(result[key], sourceValue);
      }
    });

    return result;
  }
}
